import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import rolesservice from "../../services/desiriRoles/rolesservice";
import { InputText } from "primereact/inputtext";

interface Role {
  id: number;
  name: string;
  description?: string;
}

interface Props {
  data: any[];
  onRefresh: () => void;
}
const DesiriRolesTable: React.FC<Props> = ({ data, onRefresh }) => {
  const toast = useRef<Toast>(null);
  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    const mappedRoles: Role[] = data.map((item: any) => ({
      id: item.id ?? 0,
      name: item.name ?? "",
      description: item.description ?? "",
    }));
    setRoles(mappedRoles);
  }, [data]);

  const sortedData = [...roles].sort((a, b) => b.id - a.id);

  const confirmDelete = (rowData: Role) => {
    confirmDialog({
      message: `Are you sure you want to delete "${rowData.name}"?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
    });
  };

  const handleDelete = async (rowData: Role) => {
    try {
      const res = await rolesservice.deleteRole(rowData.id);
      if (res.isSuccess) {
        setRoles((prev) => prev.filter((r) => r.id !== rowData.id));
        toast.current?.show({
          severity: "success",
          summary: "Delete Role",
          detail: res.message,
          life: 3000,
        });
        onRefresh();
      } else {
        toast.current?.show({
          severity: "warn",
          summary: "Delete Role",
          detail: res.message || "Failed to delete role.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.current?.show({
        severity: "error",
        summary: "Delete Role",
        detail: "An error occurred while deleting the role.",
        life: 3000,
      });
    }
  };
  const onRowEditComplete = async (event: any) => {
    const { newData, index } = event;

    const errors: string[] = [];
    if (!newData.name?.trim()) errors.push("Role Name is required");

    if (errors.length > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: errors.join(", "),
        life: 4000,
      });
      return;
    }

    let updatedRecord = { ...roles[index], ...newData };

    try {
      const res = await rolesservice.updateRole(updatedRecord);
      if (res.isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Update Role",
          detail: res.message,
          life: 3000,
        });
        await onRefresh();
      } else {
        toast.current?.show({
          severity: "warn",
          summary: "Update Role",
          detail: res.message || "Failed to update role.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.current?.show({
        severity: "error",
        summary: "Update Role",
        detail: "An error occurred while updating the role.",
        life: 3000,
      });
    }
  };
  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        style={{ width: "90%", border: "1px solid #090" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
      />
    );
  };
  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card p-2">
        <DataTable
          value={sortedData}
          dataKey="id"
          paginator
          rows={5}
          editMode="row"
          onRowEditComplete={onRowEditComplete}
          stripedRows
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="name"
            header="Designation Name"
            sortable
            style={{ width: "25%" }}
            editor={textEditor}
          />
          <Column
            field="description"
            header="Description"
            sortable
            style={{ width: "25%" }}
            editor={textEditor}
          />

          <Column
            rowEditor
            header="Action"
            headerStyle={{ textAlign: "right" }}
          />
          <Column
            header="Delete"
            body={(rowData: Role) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-danger p-button-text"
              />
            )}
            style={{ width: "10%" }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default DesiriRolesTable;
